import { definedMap } from "@glideapps/ts-necessities";

import { SerializedComponent } from "../../components/SerializedApp";
import { makeText } from "../builder-utils";
import {
    AppDescriptionContext,
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeTextPropertyDescriptor } from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    userProfileScreenName,
} from "../description";
import { titleProperties } from "../description-utils";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler } from "./base";
import { makeNavigationPropertyDescriptors, navigationTargetModalPropertyHandler } from "./link-to-screen";
import { asMaybeString } from "../../computation-model/data";

export interface PushUserProfileScreenActionDescription extends ActionDescription {
    readonly kind: ActionKind.PushUserProfileScreen;
    readonly title: PropertyDescription | undefined;
}

export class PushUserProfileScreenActionHandler extends BaseActionHandler<PushUserProfileScreenActionDescription> {
    public readonly kind = ActionKind.PushUserProfileScreen;
    public readonly iconName = true;

    public getDescriptor(
        _desc: PushUserProfileScreenActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Show User Profile Screen",
            needsScreenContext: false,
            properties: [
                makeTextPropertyDescriptor("title", "Title", "Page title", true, mutatingScreenKind, {
                    preferredNames: titleProperties,
                    propertySection: PropertySection.Design,
                    searchable: false,
                }),
                ...makeNavigationPropertyDescriptors(ccc, true),
            ],
        };
    }

    public getScreensUsed(
        _desc: PushUserProfileScreenActionDescription,
        adc: AppDescriptionContext
    ): readonly string[] {
        if (adc.userProfileTableInfo === undefined) return [];

        return [userProfileScreenName];
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: PushUserProfileScreenActionDescription,
        tables: InputOutputTables | undefined,
        containingScreenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;

        if (builder.userProfileTableInfo === undefined) return undefined;

        const title = definedMap(tables?.input, t =>
            makeText(builder, desc.title, t, true, true, false, false, containingScreenContext)
        );

        const context = title !== undefined ? b.object({ title: b.clonePrimitive(title) }) : b.data({});
        return b.pushModalBuilderScreen(userProfileScreenName, context, undefined, false, false);
    }

    public getTokenizedDescription(): readonly DescriptionToken[] | undefined {
        return [];
    }

    public inflate(
        ib: WireInflationBackend,
        desc: PushUserProfileScreenActionDescription
    ): WireActionHydrator | undefined {
        const [titleGetter] = ib.getValueGetterForProperty(desc.title, false);
        const target = navigationTargetModalPropertyHandler.getEnum(desc);

        return vp => {
            const title = asMaybeString(titleGetter(vp));

            return async ab => {
                ab.pushUserProfileScreen(title, target);
                return true;
            };
        };
    }
}
